import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import { IoClose, IoChevronDown, IoChevronUp } from 'react-icons/io5';
import DoubleDateRangeInput from '../Fields/DoubleDateRangeInput';
import GetIcon from '../../utils/Icon';

const ListFilters = ({
    filtersConfig,
    currentFilters = {},
    onFilterChange,
    setShowFilters,
    className = '',
    inputClassName = 'form-control',
    inputStyle = { fontSize: '0.8rem' }
}) => {
    const [filterValues, setFilterValues] = useState(() => {
        return filtersConfig.reduce((acc, filter) => {
            acc[filter.name] = currentFilters[filter.name] !== undefined
                ? currentFilters[filter.name]
                : filter.defaultValue || (filter.multiSelect ? [] : '');
            return acc;
        }, {});
    });

    const [dropdownOpen, setDropdownOpen] = useState({});
    const [searchTerms, setSearchTerms] = useState({});
    const filtersRef = useRef(null);

    useEffect(() => {
        setFilterValues(prev => {
            return filtersConfig.reduce((acc, filter) => {
                acc[filter.name] = currentFilters[filter.name] !== undefined
                    ? currentFilters[filter.name]
                    : prev[filter.name] || filter.defaultValue || (filter.multiSelect ? [] : '');
                return acc;
            }, {});
        });
    }, [currentFilters, filtersConfig]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilterValues(prev => ({ ...prev, [name]: value }));
    };

    const toggleDropdown = (name) => {
        setDropdownOpen(prev => ({ ...prev, [name]: !prev[name] }));
        if (!dropdownOpen[name]) {
            setSearchTerms(prev => ({ ...prev, [name]: '' }));
        }
    };

    const toggleOptionSelection = (name, value) => {
        const current = filterValues[name] || [];
        const updated = current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value];
        setFilterValues(prev => ({ ...prev, [name]: updated }));
    };

    const isOptionSelected = (name, value) =>
        (filterValues[name] || []).includes(value);

    const filteredOptions = (filter) => {
        const term = (searchTerms[filter.name] || '').toLowerCase();
        return filter.options?.filter(opt =>
            opt.label.toLowerCase().includes(term)
        ) || [];
    };

    const resetFilters = () => {
        const resetValues = filtersConfig.reduce((acc, filter) => {
            acc[filter.name] = filter.defaultValue || (filter.multiSelect ? [] : '');
            return acc;
        }, {});
        setFilterValues(resetValues);
    };

    return (
        <div className={`position-relative ${className}`} ref={filtersRef}>
            <div
                className="bg-white border rounded shadow-sm p-3"
                style={{
                    position: 'absolute',
                    top: '100%',
                    right: 20,
                    zIndex: 1000,
                    width: '400px'
                }}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="m-0">Filters</h6>
                    <button
                        style={{ color: 'black' }}
                        className="btn btn-sm btn-link p-0"
                        onClick={() => setShowFilters(false)}
                    >
                        <IoClose size={16} />
                    </button>
                </div>

                <div
                    className="filter-section"
                    style={{
                        maxHeight: '400px',
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#FC8B5C #f1f1f1',
                        paddingRight: '10px'
                    }}
                >
                    {filtersConfig.map(filter => (
                        <div key={filter.name} className="mb-3">
                            <div className="d-flex align-items-center">
                                <label
                                    className="form-label small fw-medium me-2"
                                    style={{ minWidth: '100px', fontSize: '13px' }}
                                >
                                    {filter.label}
                                </label>

                                <div className="flex-grow-1 w-100">
                                    {['text', 'number', 'date', 'time'].includes(filter.type) && (
                                        <input
                                            type={filter.type}
                                            name={filter.name}
                                            className={`${inputClassName} form-control-sm w-100`}
                                            value={filterValues[filter.name] || ''}
                                            onChange={handleChange}
                                            placeholder={filter.placeholder || `Enter ${filter.label}`}
                                            style={{
                                                height: '32px',
                                                ...inputStyle
                                            }}
                                        />
                                    )}

                                    {filter.type === 'select' && (
                                        <select
                                            name={filter.name}
                                            className={`${inputClassName} form-select-sm w-100`}
                                            value={filterValues[filter.name] || ''}
                                            onChange={handleChange}
                                            style={{
                                                height: '32px',
                                                ...inputStyle
                                            }}
                                        >
                                            <option value="">Select {filter.label}</option>
                                            {filter.options?.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    )}

                                    {filter.type === 'selectsearch' && (
                                        <div className="position-relative w-100">
                                            <div
                                                className={`d-flex align-items-center`}
                                                style={{
                                                    cursor: 'pointer',
                                                    height: '32px',
                                                    padding: '0.375rem 0.75rem',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '0.25rem',
                                                    ...inputStyle
                                                }}
                                                onClick={() => toggleDropdown(filter.name)}
                                            >
                                                <span className="text-truncate">
                                                    {(filterValues[filter.name]?.length > 0)
                                                        ? `${filterValues[filter.name].length} selected`
                                                        : (filter.placeholder || `Select ${filter.label}`)}
                                                </span>
                                                <span className="ms-auto ps-2">
                                                    {dropdownOpen[filter.name] ? (
                                                        <IoChevronUp size={14} />
                                                    ) : (
                                                        <IoChevronDown size={14} />
                                                    )}
                                                </span>
                                            </div>

                                            {dropdownOpen[filter.name] && (
                                                <div
                                                    className="position-absolute w-100 bg-white border rounded mt-1 shadow-sm"
                                                    style={{ zIndex: 1050 }}
                                                >
                                                    <div className="p-2 border-bottom">
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder={`Search ${filter.label}...`}
                                                            value={searchTerms[filter.name] || ''}
                                                            onChange={(e) =>
                                                                setSearchTerms(prev => ({
                                                                    ...prev,
                                                                    [filter.name]: e.target.value
                                                                }))
                                                            }
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                    <div
                                                        style={{
                                                            maxHeight: '200px',
                                                            overflowY: 'auto',
                                                            scrollbarWidth: 'thin',
                                                            scrollbarColor: '#FC8B5C #f1f1f1'
                                                        }}
                                                    >
                                                        {filteredOptions(filter).length > 0 ? (
                                                            filteredOptions(filter).map(option => (
                                                                <div
                                                                    key={option.value}
                                                                    className={`d-flex align-items-center px-3 py-2 ${isOptionSelected(filter.name, option.value) ? 'bg-light' : ''
                                                                        }`}
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        borderLeft: isOptionSelected(filter.name, option.value)
                                                                            ? '3px solid #0d6efd'
                                                                            : '3px solid transparent'
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        toggleOptionSelection(filter.name, option.value);
                                                                    }}
                                                                >
                                                                    {filter.multiSelect && (
                                                                        <input
                                                                            type="checkbox"
                                                                            className="form-check-input me-2"
                                                                            checked={isOptionSelected(filter.name, option.value)}
                                                                            readOnly
                                                                        />
                                                                    )}
                                                                    <span>{option.label}</span>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-center py-2 text-muted">
                                                                No options found
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {filter.type === 'date-range' && (
                                        <div className="w-100">
                                            <DoubleDateRangeInput
                                                name={filter.name}
                                                value={filterValues[filter.name]}
                                                onChange={handleChange}
                                                inputClassName={inputClassName}
                                                placeholder={filter.placeholder || filter.label}
                                                label={filter.label}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="d-flex  justify-content-between align-items-center gap-1 mt-3 pt-2 border-top">
                    <div className={`d-flex gap-1  align-items-center`}>
                        {/* <button className='custom-btn-104 d-flex'>
                            <GetIcon
                                iconName={"MdAdd"}
                            /> Add Filter</button> */}
                    </div>
                    <div className='d-flex gap-1 align-items-center'>
                        <button
                            className="custom-btn-102 d-flex"
                            onClick={resetFilters}
                    >
                        Clear Filters
                    </button>
                    <button
                        className="custom-btn-00989 d-flex"
                        style={{  borderColor: '#BE849D' }}
                        onClick={() => {
                            onFilterChange?.(filterValues);
                            setShowFilters(false);
                        }}
                    >
                        Apply Filters
                    </button>
                    </div>
                    
                </div>

                <style>{`
                    .filter-section::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                    }
                    .filter-section::-webkit-scrollbar-track {
                        background: #f1f1f1;
                        border-radius: 3px;
                    }
                    .filter-section::-webkit-scrollbar-thumb {
                        background: #FC8B5C;
                        border-radius: 3px;
                    }
                    .filter-section::-webkit-scrollbar-thumb:hover {
                        background: #e07b00;
                    }
                `}</style>
            </div>
        </div>
    );
};

ListFilters.propTypes = {
    filtersConfig: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.oneOf(['text', 'number', 'date', 'select', 'selectsearch', 'date-range']).isRequired,
            placeholder: PropTypes.string,
            defaultValue: PropTypes.any,
            multiSelect: PropTypes.bool,
            options: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.any.isRequired,
                    label: PropTypes.string.isRequired
                })
            )
        })
    ).isRequired,
    currentFilters: PropTypes.object,
    onFilterChange: PropTypes.func,
    setShowFilters: PropTypes.func.isRequired,
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    inputStyle: PropTypes.object
};

export default ListFilters;
